import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customersAPI } from '../services/api';
import { format } from 'date-fns';

const tierColors = {
  standard: { bg: '#f3f4f6', color: '#374151' },
  premium: { bg: '#dbeafe', color: '#1d4ed8' },
  vip: { bg: '#fef3c7', color: '#b45309' },
  enterprise: { bg: '#ede9fe', color: '#6d28d9' }
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const params = { search };
      if (tierFilter) params.tier = tierFilter;
      const response = await customersAPI.list(params);
      setCustomers(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const handleTierChange = async (customerId, newTier) => {
    try {
      await customersAPI.update(customerId, { tier: newTier });
      setCustomers(customers.map(c =>
        c.id === customerId ? { ...c, tier: newTier } : c
      ));
    } catch (err) {
      console.error('Failed to update tier:', err);
      alert('Failed to update tier');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-description">Manage your customers and their licenses</p>
        </div>
        <Link to="/dashboard/customers/new" className="btn btn-primary">
          Add Customer
        </Link>
      </div>

      <div className="card">
        <form onSubmit={handleSearch} className="search-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <select
            className="form-input"
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); }}
            style={{ width: '150px' }}
          >
            <option value="">All Tiers</option>
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="vip">VIP</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <button type="submit" className="btn btn-secondary">Search</button>
        </form>

        {loading ? (
          <div className="loading-container"><div className="spinner"></div></div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Licenses</th>
                  <th>Version</th>
                  <th>Last Seen</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <Link to={`/dashboard/customers/${customer.id}`} className="link font-medium">
                        {customer.company_name}
                      </Link>
                    </td>
                    <td>{customer.contact_name}</td>
                    <td>{customer.email}</td>
                    <td>
                      <select
                        value={customer.tier || 'standard'}
                        onChange={(e) => handleTierChange(customer.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '9999px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          backgroundColor: tierColors[customer.tier]?.bg || '#f3f4f6',
                          color: tierColors[customer.tier]?.color || '#374151',
                          border: 'none',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                        <option value="vip">VIP</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </td>
                    <td>
                      <span className={`badge badge-${customer.status === 'active' ? 'success' : 'gray'}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-gray">
                        {customer.active_licenses || 0} / {customer.total_licenses || 0}
                      </span>
                    </td>
                    <td>
                      {customer.app_version ? (
                        <span className="badge badge-info" style={{fontSize: '11px'}}>{customer.app_version}</span>
                      ) : (
                        <span style={{color: '#9ca3af', fontSize: '12px'}}>—</span>
                      )}
                    </td>
                    <td style={{fontSize: '12px', color: '#6b7280'}}>
                      {customer.last_seen_at ? format(new Date(customer.last_seen_at), 'MMM d, HH:mm') : '—'}
                    </td>
                    <td>{format(new Date(customer.created_at), 'MMM d, yyyy')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
